// One work item as its OWN routed screen (deep-linkable, browser-back friendly):
// /app/work/:itemId. The surface mode is DERIVED, not a route: `?review=1`
// (Reviews tab, notification links) → reviewer; otherwise a work:approve holder
// who is not the item's owner reviews, everyone else acts. The backend state
// machine + SoD stay authoritative regardless of which affordances render.
// Exception (13-completion-policy.md): an owner who can review still gets the
// reviewer panel once their OWN item is awaiting review — otherwise a
// self-assigned item that was submitted shows an empty Actions panel and can
// never be finished by anyone. SoD is not weakened here: the server refuses
// ("you cannot review your own work") wherever review genuinely is required,
// and that refusal already surfaces as a toast.
import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { DiscussButton } from '@/features/conversations/DiscussButton';
import { useHasPermission } from '@/rbac/useRbac';
import { useMyEmployeeId } from '@/features/work/api/hooks';
import { syncWorkDiscussion, type WorkItem, type WorkStatus } from '@/services/workService';
import { WorkItemSurface } from './shared';

export const WorkItemPage: React.FC = () => {
  const { t } = useTranslation('work');
  const { itemId } = useParams<{ itemId: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const canApprove = useHasPermission('work:approve');
  const myEmployeeId = useMyEmployeeId();
  const [itemTitle, setItemTitle] = useState('');
  const [owner, setOwner] = useState<string | null | undefined>(undefined);
  const [status, setStatus] = useState<WorkStatus | undefined>(undefined);
  const forceReview = params.get('review') === '1';
  const isOwner = !!owner && !!myEmployeeId && owner === myEmployeeId;
  const awaitingReview = status === 'submitted_for_completion' || status === 'under_review';
  // Until the item has loaded we don't know the owner; a reviewer-capable user
  // defaults to reviewer (their common case), everyone else to actor. An owner
  // acts on their own item — until it is submitted, when only the reviewer panel
  // has anything left to offer.
  const mode: 'actor' | 'reviewer' =
    forceReview || (canApprove && (!isOwner || awaitingReview)) ? 'reviewer' : 'actor';
  if (!itemId) return null;
  return (
    <div className="p-6 max-w-5xl space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" className="-ml-2"
          onClick={() => navigate(forceReview ? '..?tab=reviews' : '..', { relative: 'path' })}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          {forceReview ? t('tab_reviews', { defaultValue: 'Reviews' }) : t('page_title', { defaultValue: 'Work' })}
        </Button>
        {/* Jump into this work item's discussion (slide-over — this screen stays put). */}
        <DiscussButton
          contextType="WORK_ITEM"
          contextId={itemId}
          title={itemTitle || 'Work item'}
          beforeOpen={() => syncWorkDiscussion(itemId)}
        />
      </div>
      <WorkItemSurface
        id={itemId}
        mode={mode}
        openItem={(next) => navigate(`../${next}${forceReview ? '?review=1' : ''}`, { relative: 'path' })}
        onChanged={() => undefined}
        onTitle={setItemTitle}
        onLoaded={(w: WorkItem) => { setOwner(w.owner_employee_id ?? null); setStatus(w.status); }}
      />
    </div>
  );
};

export default WorkItemPage;
